/**
 * @file `TagMultiSelect` — a reusable include/exclude tag picker: the
 *       selected tags as removable chips + a datalist-backed add input.
 * @module @labcraft/admin/ui/learners/tag-filter
 *
 * Shared by the Learners roster filter and the Emails compose targeting
 * (#1639) so the two tag pickers behave identically. Purely controlled: the
 * parent owns the selected array and supplies the suggestion source (from
 * `useAllTags`). Tags are forwarded raw; the account daemon canonicalizes.
 */

import { useState, type JSX } from 'react'

/** Props for {@link TagMultiSelect}. */
export type TagMultiSelectProps = {
  label: string
  selected: string[]
  onChange: (next: string[]) => void
  suggestions: string[]
  datalistId: string
  placeholder?: string
}

/**
 * Render a controlled multi-tag picker. Selected tags show as removable
 * chips; the input offers a datalist over the not-yet-selected suggestions
 * and commits on Enter. Duplicates and blanks are ignored.
 *
 * @param props - Label, the controlled selection + change handler, the
 *   suggestion list, and a unique `datalistId` (two instances on one page
 *   must not share it).
 */
export function TagMultiSelect({
  label,
  selected,
  onChange,
  suggestions,
  datalistId,
  placeholder,
}: TagMultiSelectProps): JSX.Element {
  const [draft, setDraft] = useState('')
  const available = suggestions.filter(t => !selected.includes(t))

  const add = (raw: string): void => {
    const value = raw.trim()
    setDraft('')
    if (value === '' || selected.includes(value)) return
    onChange([...selected, value])
  }
  const remove = (tag: string): void => {
    onChange(selected.filter(t => t !== tag))
  }

  return (
    <div className="tag-filter">
      <span className="field-label">{label}</span>
      {selected.length > 0 && (
        <div className="tag-chips">
          {selected.map(t => (
            <span key={t} className="tag-chip">
              <span className="tag-chip__label">{t}</span>
              <button
                type="button"
                className="tag-chip__x"
                aria-label={`Remove ${label} ${t}`}
                onClick={() => remove(t)}>
                ✕
              </button>
            </span>
          ))}
        </div>
      )}
      <input
        className="lc-input"
        type="text"
        list={datalistId}
        placeholder={placeholder ?? 'tag…'}
        aria-label={label}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault()
            add(draft)
          }
        }}
      />
      <datalist id={datalistId}>
        {available.map(t => (
          <option key={t} value={t} />
        ))}
      </datalist>
    </div>
  )
}
